import {
  ALLOWED_NATIVE_EXTENSIONS,
  MAX_NATIVE_SIZE_BYTES,
} from "@etudescarre/constants";
import { Button, Select } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { combineDateAndTime, nowLocalTime, todayLocal } from "@/lib/utils/date";
import { useUnsavedChangesGuard } from "@/lib/utils/unsaved-changes";
import { useYardContext } from "@/providers/yard-context";

// Add a maquette (without documents) to the rang 2 proposition.
// Mirrors ProposeDocumentsPanel: the maquette is registered through the reused
// reception route, the emetteur token being the caller's own rang 2 contract.
// Two modes: a brand new maquette, or a new version of an
// existing maquette of the contract (number selected in a coherent dropdown,
// name pre-filled and locked, interventions pre-filled but editable).

type Intervention = {
  id: number;
  zoneName: string;
  zoneType: string | null;
  buildingName: string | null;
  performanceName: string;
};

type ExistingMaquette = {
  id: number;
  number: string | null;
  name: string;
  interventionIds: number[];
};

type MaquetteFormData = {
  number: string;
  name: string;
  dateEtat: string;
  heureEtat: string;
  interventionIds: number[];
};

type Mode = "new" | "version";

const IFC_EXTENSION = "ifc";

const inputClass =
  "w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100";

const labelClass =
  "mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100";

const fileInputClass =
  "w-full text-sm text-zinc-500 file:mr-4 file:rounded-md file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-blue-700 hover:file:bg-blue-100 dark:text-zinc-400 dark:file:bg-blue-900/20 dark:file:text-blue-400";

type ProposeMaquettePanelProps = {
  onClose: () => void;
};

export function ProposeMaquettePanel({ onClose }: ProposeMaquettePanelProps) {
  const { yard, contracts } = useYardContext();

  // Only travaux rang 2 contracts held by the current user can propose.
  const rang2Contracts = contracts.filter((c) => c.type === "travaux_n_2");

  const [contractId, setContractId] = useState<number | null>(
    rang2Contracts.length === 1 ? rang2Contracts[0].id : null,
  );
  const [mode, setMode] = useState<Mode>("new");
  const [interventions, setInterventions] = useState<Intervention[]>([]);
  const [loadingInterventions, setLoadingInterventions] = useState(false);
  const [existingMaquettes, setExistingMaquettes] = useState<
    ExistingMaquette[]
  >([]);
  const [existingMaquetteId, setExistingMaquetteId] = useState<number | null>(
    null,
  );
  const [ifcFile, setIfcFile] = useState<File | null>(null);
  const [nativeFile, setNativeFile] = useState<File | null>(null);
  const [proposed, setProposed] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const maquetteForm = useForm<MaquetteFormData>({
    defaultValues: {
      number: "",
      name: "",
      dateEtat: todayLocal(),
      heureEtat: nowLocalTime(),
      interventionIds: [],
    },
  });

  const number = maquetteForm.watch("number");
  const name = maquetteForm.watch("name");
  const selectedInterventions = maquetteForm.watch("interventionIds");

  // Warn before losing the in-progress maquette draft (refresh, nav, etc.).
  const isDirty =
    number.trim().length > 0 ||
    name.trim().length > 0 ||
    selectedInterventions.length > 0 ||
    isNotNil(existingMaquetteId) ||
    isNotNil(ifcFile) ||
    isNotNil(nativeFile);
  useUnsavedChangesGuard(isDirty);

  const contractOptions = rang2Contracts.map((c) => ({
    label: `${c.performerCompanyName} (${c.code ?? c.type})`,
    value: String(c.id),
  }));

  // The "N° existant" dropdown only offers maquettes with a number.
  const maquetteOptions = existingMaquettes
    .filter((m) => isNotNil(m.number))
    .map((m) => ({ label: `${m.number} — ${m.name}`, value: String(m.id) }));

  const selectedMaquette = existingMaquettes.find(
    (m) => m.id === existingMaquetteId,
  );

  const fetchContractData = useCallback(
    async (id: number) => {
      setLoadingInterventions(true);
      try {
        const [interventionsRes, maquettesRes] = await Promise.all([
          apiClient.GET("/api/yards/{yardId}/receptions/interventions", {
            params: {
              path: { yardId: String(yard.id) },
              query: { contractId: String(id) },
            },
          }),
          apiClient.GET("/api/yards/{yardId}/receptions/maquettes", {
            params: {
              path: { yardId: String(yard.id) },
              query: { contractId: String(id) },
            },
          }),
        ]);
        if (isNotNil(interventionsRes.data)) {
          setInterventions(interventionsRes.data.data.interventions);
        }
        if (isNotNil(maquettesRes.data)) {
          setExistingMaquettes(maquettesRes.data.data.maquettes);
        }
      } catch {
        // ignore
      } finally {
        setLoadingInterventions(false);
      }
    },
    [yard.id],
  );

  useEffect(() => {
    if (isNotNil(contractId)) {
      fetchContractData(contractId);
    }
  }, [contractId, fetchContractData]);

  const selectMode = (nextMode: Mode) => {
    setMode(nextMode);
    setExistingMaquetteId(null);
    maquetteForm.reset();
  };

  const selectExistingMaquette = (id: number | null) => {
    setExistingMaquetteId(id);
    const maquette = existingMaquettes.find((m) => m.id === id);
    // Pre-fill from the previous version: name locked, interventions editable.
    maquetteForm.setValue("number", maquette?.number ?? "");
    maquetteForm.setValue("name", maquette?.name ?? "");
    maquetteForm.setValue("interventionIds", maquette?.interventionIds ?? []);
  };

  const handleSubmit = maquetteForm.handleSubmit(async (data) => {
    if (isNil(contractId)) {
      toaster.error({
        title: "Erreur",
        description: "Sélectionnez un contrat",
      });
      return;
    }
    if (mode === "version" && isNil(selectedMaquette)) {
      toaster.error({
        title: "Erreur",
        description: "Sélectionnez une maquette existante",
      });
      return;
    }
    if (data.interventionIds.length === 0) {
      toaster.error({
        title: "Erreur",
        description: "Sélectionnez au moins une intervention",
      });
      return;
    }

    const dateEtat = combineDateAndTime(data.dateEtat, data.heureEtat);
    if (isNil(dateEtat)) {
      toaster.error({
        title: "Erreur",
        description: "Renseignez une date et une heure d'état valides",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const metadata = {
        number: data.number,
        name: data.name,
        dateEtat,
        interventionIds: data.interventionIds,
        ...(mode === "version" && isNotNil(selectedMaquette)
          ? { existingBimModelId: selectedMaquette.id }
          : {}),
      };

      const { error } = await apiClient.POST(
        "/api/yards/{yardId}/receptions/{receptionId}/maquettes",
        {
          params: {
            path: {
              yardId: String(yard.id),
              // The rang 2 contract is its own emetteur ("reception") token.
              receptionId: String(contractId),
            },
          },
          body: {
            metadata: JSON.stringify(metadata),
            ifc: ifcFile ?? undefined,
            native: nativeFile ?? undefined,
          },
          bodySerializer(body) {
            const fd = new FormData();
            for (const [key, value] of Object.entries(
              body as Record<string, unknown>,
            )) {
              if (value instanceof File) {
                fd.append(key, value, value.name);
              } else if (isNotNil(value)) {
                fd.append(key, String(value));
              }
            }
            return fd;
          },
        },
      );

      if (error) {
        toaster.error({
          title: "Erreur",
          description:
            error.error ??
            "La proposition n'a pas pu être enregistrée, réessayez",
        });
        return;
      }

      setProposed((prev) => prev + 1);
      toaster.success({
        title: "Succès",
        description: "Maquette ajoutée à la proposition",
      });

      maquetteForm.reset();
      setExistingMaquetteId(null);
      setIfcFile(null);
      setNativeFile(null);
      if (isNotNil(contractId)) {
        // Refresh the existing maquettes so a new number becomes selectable.
        fetchContractData(contractId);
      }
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Une erreur est survenue",
      });
    } finally {
      setIsSubmitting(false);
    }
  });

  const handleFileChange =
    (
      setter: (f: File | null) => void,
      allowed: readonly string[],
      maxLabel: string,
    ) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (isNil(file)) return;
      const ext = file.name.split(".").pop()?.toLowerCase();
      if (isNil(ext) || !allowed.includes(ext)) {
        toaster.error({
          title: "Erreur",
          description: `Extension non autorisée. Formats acceptés : ${allowed.join(", ")}`,
        });
        e.target.value = "";
        return;
      }
      if (file.size > MAX_NATIVE_SIZE_BYTES) {
        toaster.error({
          title: "Erreur",
          description: `Le fichier dépasse ${maxLabel}`,
        });
        e.target.value = "";
        return;
      }
      setter(file);
    };

  const toggleIntervention = (id: number) => {
    if (selectedInterventions.includes(id)) {
      maquetteForm.setValue(
        "interventionIds",
        selectedInterventions.filter((i) => i !== id),
      );
    } else {
      maquetteForm.setValue("interventionIds", [...selectedInterventions, id]);
    }
  };

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onClose}
        className="text-sm text-blue-600 hover:underline dark:text-blue-400"
      >
        Retour aux actions
      </button>

      <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
        Ajouter une maquette à la proposition
      </h3>

      {rang2Contracts.length === 0 ? (
        <p className="text-sm text-zinc-500">
          Aucun contrat travaux rang 2 ne vous permet de proposer des maquettes.
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {proposed > 0 && (
            <div className="rounded-md bg-green-50 p-3 text-sm text-green-800 dark:bg-green-900/20 dark:text-green-300">
              {proposed} maquette(s) ajoutée(s) à la proposition
            </div>
          )}

          <div>
            <span className={labelClass}>
              Contrat rang 2 <span className="text-red-500">*</span>
            </span>
            <Select
              options={contractOptions}
              value={isNotNil(contractId) ? [String(contractId)] : []}
              onValueChange={(details) => {
                const val = details.value[0];
                setContractId(val ? Number(val) : null);
                setExistingMaquetteId(null);
              }}
              placeholder="Sélectionnez votre contrat"
            />
          </div>

          <div>
            <span className={labelClass}>Type d'ajout</span>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  checked={mode === "new"}
                  onChange={() => selectMode("new")}
                  className="text-blue-600"
                />
                Nouvelle maquette
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  checked={mode === "version"}
                  onChange={() => selectMode("version")}
                  className="text-blue-600"
                />
                Nouvelle version d'une maquette existante
              </label>
            </div>
          </div>

          {mode === "new" ? (
            <>
              <div>
                <label htmlFor="proposeMaqNumber" className={labelClass}>
                  N° de la maquette <span className="text-red-500">*</span>
                </label>
                <input
                  id="proposeMaqNumber"
                  type="text"
                  {...maquetteForm.register("number")}
                  required
                  minLength={1}
                  maxLength={255}
                  className={inputClass}
                />
              </div>

              <div>
                <label htmlFor="proposeMaqName" className={labelClass}>
                  Intitulé <span className="text-red-500">*</span>
                </label>
                <input
                  id="proposeMaqName"
                  type="text"
                  {...maquetteForm.register("name")}
                  required
                  minLength={1}
                  maxLength={255}
                  className={inputClass}
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <span className={labelClass}>
                  N° de la maquette existante{" "}
                  <span className="text-red-500">*</span>
                </span>
                {maquetteOptions.length === 0 ? (
                  <p className="text-sm text-zinc-500">
                    Aucune maquette existante pour ce contrat
                  </p>
                ) : (
                  <Select
                    options={maquetteOptions}
                    value={
                      isNotNil(existingMaquetteId)
                        ? [String(existingMaquetteId)]
                        : []
                    }
                    onValueChange={(details) => {
                      const val = details.value[0];
                      selectExistingMaquette(val ? Number(val) : null);
                    }}
                    placeholder="Sélectionnez une maquette"
                  />
                )}
              </div>

              {isNotNil(selectedMaquette) && (
                <div>
                  <span className={labelClass}>Intitulé</span>
                  <p className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-700 dark:border-zinc-700 dark:bg-zinc-800/50 dark:text-zinc-300">
                    {selectedMaquette.name}
                  </p>
                </div>
              )}
            </>
          )}

          <div className="flex gap-3">
            <div className="flex-1">
              <label htmlFor="proposeMaqDateEtat" className={labelClass}>
                Date d'état <span className="text-red-500">*</span>
              </label>
              <input
                id="proposeMaqDateEtat"
                type="date"
                {...maquetteForm.register("dateEtat")}
                required
                className={inputClass}
              />
            </div>
            <div className="flex-1">
              <label htmlFor="proposeMaqHeureEtat" className={labelClass}>
                Heure d'état <span className="text-red-500">*</span>
              </label>
              <input
                id="proposeMaqHeureEtat"
                type="time"
                {...maquetteForm.register("heureEtat")}
                required
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <span className={labelClass}>
              Niveaux et façades <span className="text-red-500">*</span>
            </span>
            {loadingInterventions ? (
              <p className="text-sm text-zinc-500">Chargement...</p>
            ) : interventions.length === 0 ? (
              <p className="text-sm text-zinc-500">
                Aucune intervention trouvée pour ce contrat
              </p>
            ) : (
              <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-zinc-300 p-2 dark:border-zinc-700">
                {interventions.map((intv) => (
                  <label
                    key={intv.id}
                    className="flex items-center gap-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={selectedInterventions.includes(intv.id)}
                      onChange={() => toggleIntervention(intv.id)}
                    />
                    <span>
                      {intv.buildingName ? `${intv.buildingName} - ` : ""}
                      {intv.zoneName} / {intv.performanceName}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <label htmlFor="proposeMaqIfc" className={labelClass}>
              Fichier IFC
            </label>
            <input
              id="proposeMaqIfc"
              type="file"
              accept={`.${IFC_EXTENSION}`}
              onChange={handleFileChange(setIfcFile, [IFC_EXTENSION], "200 Mo")}
              className={fileInputClass}
            />
            {isNotNil(ifcFile) && (
              <p className="mt-1 text-xs text-zinc-500">
                {ifcFile.name} ({(ifcFile.size / 1024 / 1024).toFixed(1)} Mo)
              </p>
            )}
          </div>

          <div>
            <label htmlFor="proposeMaqNative" className={labelClass}>
              Fichier natif
            </label>
            <input
              id="proposeMaqNative"
              type="file"
              accept={ALLOWED_NATIVE_EXTENSIONS.map((e) => `.${e}`).join(",")}
              onChange={handleFileChange(
                setNativeFile,
                ALLOWED_NATIVE_EXTENSIONS,
                "200 Mo",
              )}
              className={fileInputClass}
            />
            {isNotNil(nativeFile) && (
              <p className="mt-1 text-xs text-zinc-500">
                {nativeFile.name} ({(nativeFile.size / 1024 / 1024).toFixed(1)}{" "}
                Mo)
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              outline
              onClick={onClose}
              disabled={isSubmitting}
            >
              Terminer
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "En cours..." : "Ajouter à la proposition"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
